import React, { useState, useEffect } from 'react';
import { Search, Mail, Trash2, Edit, ToggleLeft, ToggleRight, Plus, Shield } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const getErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return error?.message || fallbackMessage;
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/admins', {
        params: { page, limit: 20, search },
      });
      if (response.data.success) {
        // Filter to only show admin users (exclude superadmin)
        const adminUsers = response.data.admins.filter(admin => admin.role === 'admin');
        setAdmins(adminUsers);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load admin users'));
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedAdmin({
      email: '',
      first_name: '',
      last_name: '',
      is_active: true,
    });
    setNewPassword('');
    setShowAddModal(true);
  };

  const openEditModal = (admin) => {
    setSelectedAdmin({
      ...admin,
      first_name: admin.first_name || '',
      last_name: admin.last_name || '',
      email: admin.email || '',
      is_active: admin.is_active,
    });
    setNewPassword('');
    setShowEditModal(true);
  };

  const handleCreateAdmin = async () => {
    if (!selectedAdmin) return;
    
    if (!selectedAdmin.email || !selectedAdmin.first_name || !selectedAdmin.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/admin/admins', {
        email: selectedAdmin.email.trim(),
        firstName: selectedAdmin.first_name.trim(),
        lastName: selectedAdmin.last_name.trim(),
        role: 'admin',
        password: newPassword,
      });
      if (response.data.success) {
        toast.success('Admin user created successfully');
        setShowAddModal(false);
        setNewPassword('');
        fetchAdmins();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create admin user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;
    try {
      setSaving(true);
      const updateData = {
        firstName: selectedAdmin.first_name?.trim(),
        lastName: selectedAdmin.last_name?.trim(),
        email: selectedAdmin.email?.trim(),
        isActive: selectedAdmin.is_active ? 1 : 0,
      };
      
      // Only include password if it's provided
      if (newPassword && newPassword.length >= 6) {
        updateData.password = newPassword;
      }
      
      const response = await api.put(`/admin/admins/${selectedAdmin.id}`, updateData);
      if (response.data.success) {
        toast.success('Admin user updated successfully');
        setShowEditModal(false);
        setNewPassword('');
        fetchAdmins();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update admin user');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (admin) => {
    setDeleteTarget(admin);
    setShowConfirmModal(true);
  };

  const handleToggleActive = async (adminId, currentStatus) => {
    try {
      const response = await api.patch(`/admin/admins/${adminId}`, {
        isActive: currentStatus ? 0 : 1,
      });
      if (response.data.success) {
        toast.success(`Admin user ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchAdmins();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update admin user');
    }
  };

  const handleDelete = async (adminId) => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/admins/${adminId}`);
      if (response.data.success) {
        toast.success('Admin user deleted successfully');
        fetchAdmins();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete admin user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminSidebar>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Admin Users Management</h1>
            <p className="text-gray-600">Manage admin users</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Admin
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search admin users by email or name..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : admins.length === 0 ? (
          <EmptyState
            icon={<Shield className="h-7 w-7" />}
            title={
              search
                ? `No admin users found for "${search}"`
                : 'No admin users yet'
            }
            description={
              search
                ? 'Try adjusting your search terms or clear the filter to see all admin users.'
                : 'Add your first admin user to get started with managing the platform.'
            }
            action={
              search
                ? {
                    label: 'Clear search',
                    onClick: () => {
                      setSearch('');
                      setPage(1);
                    },
                  }
                : {
                    label: 'Add Admin User',
                    onClick: openAddModal,
                  }
            }
          />
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Admin User</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Role</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Created</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {admins.map((admin, index) => (
                      <tr 
                        key={admin.id} 
                        className="hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50 transition-all duration-150 group"
                      >
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md ring-2 ring-white">
                                {admin.first_name?.[0]?.toUpperCase() || admin.email[0].toUpperCase()}
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                              <div className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                {admin.first_name} {admin.last_name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 flex items-center truncate">
                                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{admin.email}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <div className="flex items-center">
                            <div className="px-2.5 py-1 rounded-md bg-purple-100 text-purple-700 inline-flex items-center">
                              <Shield className="h-3.5 w-3.5 mr-1.5" />
                              <span className="text-xs font-medium">
                                {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                          <div className="text-sm text-gray-600">
                            {new Date(admin.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <button
                            onClick={() => handleToggleActive(admin.id, admin.is_active)}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                              admin.is_active
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={admin.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-current"></span>
                            {admin.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => openEditModal(admin)}
                              className="p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm(admin)}
                              className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.pages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      {/* Add Admin Modal */}
      {showAddModal && selectedAdmin && (
        <Modal title="Add Admin User" onClose={() => setShowAddModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={selectedAdmin.email}
                onChange={(e) =>
                  setSelectedAdmin((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={selectedAdmin.first_name}
                onChange={(e) =>
                  setSelectedAdmin((prev) => ({ ...prev, first_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={selectedAdmin.last_name}
                onChange={(e) =>
                  setSelectedAdmin((prev) => ({ ...prev, last_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="Minimum 6 characters"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() =>
                  setSelectedAdmin((prev) => ({ ...prev, is_active: !prev.is_active }))
                }
                className="text-gray-400 hover:text-gray-600"
              >
                {selectedAdmin.is_active ? (
                  <ToggleRight className="h-6 w-6 text-green-500" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewPassword('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAdmin}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Creating...' : 'Create Admin'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Admin Modal */}
      {showEditModal && selectedAdmin && (
        <Modal title="Edit Admin User" onClose={() => setShowEditModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={selectedAdmin.email}
                onChange={(e) =>
                  setSelectedAdmin((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={selectedAdmin.first_name}
                onChange={(e) =>
                  setSelectedAdmin((prev) => ({ ...prev, first_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={selectedAdmin.last_name}
                onChange={(e) =>
                  setSelectedAdmin((prev) => ({ ...prev, last_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password (leave blank to keep current)</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="Enter new password (minimum 6 characters)"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to keep the current password</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() =>
                  setSelectedAdmin((prev) => ({ ...prev, is_active: !prev.is_active }))
                }
                className="text-gray-400 hover:text-gray-600"
              >
                {selectedAdmin.is_active ? (
                  <ToggleRight className="h-6 w-6 text-green-500" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setNewPassword('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAdmin}
                disabled={saving || (newPassword && newPassword.length > 0 && newPassword.length < 6)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmModal && deleteTarget && (
        <ConfirmDialog
          title="Delete Admin User"
          description={`Are you sure you want to delete ${deleteTarget.first_name || 'this admin user'}? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={() => {
            setShowConfirmModal(false);
            handleDelete(deleteTarget.id);
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTarget(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminAdmins;

